import { IServerPagination } from "app/model"
import { AdvertiserAPI } from "generated-api"
import { normalizeSync } from "normalize-diacritics"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import {
  IAdvertiser,
  IAdvertiserDomain,
  IAdvertiserDomainDTO,
  IAdvertiserDTO,
  IAdvertiserFilters,
  IAdvertiserRepository,
  ICreateAdvertiserDomainParams,
  ICreateAdvertiserParams,
  IUpdateAdvertiserDomainParams,
  IUpdateAdvertiserParams
} from "./model"

interface IGetAdvertisersParams {
  comparators: string[]
  match: "exact" | "contains" | "starts_with" | "ends_with"
  nameSearch: string | undefined
}

export const advertiserRPO: IAdvertiserRepository = {
  getAllAdvertisers(): Promise<IAdvertiser[]> {
    return new Promise<IAdvertiser[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.getApiAdvertiserList()
          .then(response => resolve(response.map(a => getAdvertiserFromDTO(a))))
          .catch((error: any) => {
            console.error("Server error while getting all advertisers", error)
            reject("advertiser.get.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting all advertisers", ex)
        reject("advertiser.get.all.error.unexpected")
      }
    })
  },
  getAdvertiser(advertiserId: number): Promise<IAdvertiser> {
    return new Promise<IAdvertiser>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.getApiAdvertiser(advertiserId)
          .then(response => {
            if (response === null) {
              return reject(`Advertiser id="${advertiserId}" not found`)
            }
            resolve(getAdvertiserFromDTO(response))
          })
          .catch((error: any) => {
            console.error("Server error while getting advertiser", error)
            reject("advertiser.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting advertiser", ex)
        reject("advertiser.get.all.error.unexpected")
      }
    })
  },
  getAdvertisers(pagination: IServerPagination, filters: IAdvertiserFilters): Promise<IAdvertiser[]> {
    return new Promise<IAdvertiser[]>((resolve, reject) => {
      const sort: string[] = []
      const offset = pagination.page * pagination.pageSize
      const limit = pagination.pageSize
      if (pagination.orderBy) {
        sort.push(`${pagination.orderBy},${pagination.order || "asc"}`)
      }
      const params = getGetAdvertisersParams(filters)
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.getApiAdvertiserList(
          offset,
          limit,
          params.nameSearch, // search
          params.match, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          sort.length > 0 ? sort : undefined, // order by => string[] (fieldname,[asc|desc]+)
          params.comparators // comparators
        )
          .then(response => resolve(response.map(r => getAdvertiserFromDTO(r))))
          .catch((error: any) => {
            console.error("Server error while getting advertisers", error)
            reject("advertisers.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting advertisers", ex)
        reject("advertisers.get.error.unexpected")
      }
    })
  },
  getAdvertisersCount(filters: IAdvertiserFilters): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const params = getGetAdvertisersParams(filters)
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.getApiAdvertiserCount(
          undefined, // no offset
          undefined, // no limit
          params.nameSearch, // search
          params.match, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          undefined, // order by => string[] (fieldname,[asc|desc]+)
          params.comparators // comparators
        )
          .then(response => resolve(response.count || 0))
          .catch((error: any) => {
            console.error("Server error while counting advertisers", error)
            reject("advertisers.count.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while counting advertisers", ex)
        reject("advertisers.count.error.unexpected")
      }
    })
  },
  createAdvertiser(params: ICreateAdvertiserParams): Promise<IAdvertiser> {
    return new Promise<IAdvertiser>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.postApiAdvertiser(params)
          .then(response => resolve(getAdvertiserFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while creating advertiser", error)
            reject("advertiser.creating.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating advertiser", ex)
        reject("advertiser.creating.error.unexpected")
      }
    })
  },
  updateAdvertiser(params: IUpdateAdvertiserParams): Promise<IAdvertiser> {
    return new Promise<IAdvertiser>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.putApiAdvertiser(params)
          .then(response => resolve(getAdvertiserFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while updating advertiser", error)
            reject("advertiser.updating.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while updating advertiser", ex)
        reject("advertiser.updating.error.unexpected")
      }
    })
  },
  getAdvertiserDomains(advertiserId: number): Promise<IAdvertiserDomain[]> {
    return new Promise<IAdvertiserDomain[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.getApiAdvertiserDomainList(advertiserId)
          .then(response => resolve(response.map(a => getAdvertiserDomainFromDTO(a))))
          .catch((error: any) => {
            console.error("Server error while getting advertiser domains", error)
            reject("advertiser.domain.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting advertiser domains", ex)
        reject("advertiser.domain.get.error.unexpected")
      }
    })
  },
  getAllAdvertiserDomain(): Promise<IAdvertiserDomain[]> {
    return new Promise<IAdvertiserDomain[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.getApiAdvertiserDomainList1()
          .then(response => resolve(response.map(a => getAdvertiserDomainFromDTO(a))))
          .catch((error: any) => {
            console.error("Server error while getting all advertiser domain", error)
            reject("advertiser.domain.get.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting all advertiser domain", ex)
        reject("advertiser.domain.get.all.error.unexpected")
      }
    })
  },
  createAdvertiserDomain(advertiserId: number, params: ICreateAdvertiserDomainParams): Promise<IAdvertiserDomain> {
    return new Promise<IAdvertiserDomain>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.postApiAdvertiserDomain(advertiserId, params)
          .then(response => resolve(getAdvertiserDomainFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while creating advertiser domain", error)
            reject("advertiser-domain.creating.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating advertiser domain", ex)
        reject("advertiser-domain.creating.error.unexpected")
      }
    })
  },
  updateAdvertiserDomain(params: IUpdateAdvertiserDomainParams): Promise<IAdvertiserDomain> {
    return new Promise<IAdvertiserDomain>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.putApiAdvertiserDomain(params)
          .then(response => resolve(getAdvertiserDomainFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while updating advertiser domain", error)
            reject("advertiser-domain.updating.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while updating advertiser domain", ex)
        reject("advertiser-domain.updating.error.unexpected")
      }
    })
  },
  deleteAdvertiserDomain(advertiserId: number, advertiserDomainId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        AdvertiserAPI.deleteApiAdvertiserDomain(advertiserId, advertiserDomainId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while deleting advertiser domain", error)
            reject("advertiser.domain.delete.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while deleting advertiser domain", ex)
        reject("advertiser.domain.delete.error.unexpected")
      }
    })
  }
}

const getAdvertiserDomainFromDTO = (advertiserDomainDTO: IAdvertiserDomainDTO): IAdvertiserDomain => {
  return {
    ...advertiserDomainDTO,
    normalizedName: advertiserDomainDTO.domain ? normalizeSync(advertiserDomainDTO.domain).toLowerCase() : ""
  }
}

function getAdvertiserFromDTO(advertiserDTO: IAdvertiserDTO): IAdvertiser {
  return {
    ...advertiserDTO,
    normalizedName: advertiserDTO.name ? normalizeSync(advertiserDTO.name).toLowerCase() : ""
  }
}

function getGetAdvertisersParams(filters: IAdvertiserFilters): IGetAdvertisersParams {
  const params: IGetAdvertisersParams = {
    comparators: [],
    match: "contains",
    nameSearch: filters.name || undefined
  }
  return params
}
